import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { Offer, Listing } from '../types';
import './OrphanOfferManager.css';

interface OrphanOfferManagerProps {
  orphanOffers: Offer[];
  allListings: Listing[];
  onClose: () => void;
  onOfferLinked: (offerId: string, listingId: string) => void;
}

interface CreateListingForm {
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

interface MatchCandidate {
  listing: Listing;
  matchScore: number;
  matchReasons: string[];
}

interface OrphanWithMatches {
  offer: Offer;
  candidates: MatchCandidate[];
  selectedListingId?: string;
}

/** Extract property address fields from an offer, preferring v2 extractedData over v1 combinedOfferData */
function getOfferProperty(offer: Offer): { address: string; city: string; state: string; zip: string } {
  const v2 = offer.extractedData?.property;
  if (v2?.address) {
    return {
      address: v2.address || '',
      city: v2.city || '',
      state: v2.state || '',
      zip: v2.zip || '',
    };
  }
  const v1 = offer.combinedOfferData?.purchaseAgreement?.property;
  return {
    address: v1?.address || '',
    city: v1?.city || '',
    state: v1?.state || '',
    zip: (v1 as any)?.zipCode || '',
  };
}

const OrphanOfferManager: React.FC<OrphanOfferManagerProps> = ({
  orphanOffers,
  allListings,
  onClose,
  onOfferLinked,
}) => {
  const { currentUser } = useAuth();
  const [orphansWithMatches, setOrphansWithMatches] = useState<OrphanWithMatches[]>([]);
  const [selectedOffers, setSelectedOffers] = useState<Set<string>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const [linkedCount, setLinkedCount] = useState(0);
  const [viewMode, setViewMode] = useState<'auto-match' | 'manual-link'>('auto-match');
  const [searchQuery, setSearchQuery] = useState('');
  // offerId → create-listing form state (undefined = form not open)
  const [createListingForms, setCreateListingForms] = useState<Record<string, CreateListingForm>>({});
  const [creatingListingFor, setCreatingListingFor] = useState<string | null>(null);

  // Calculate match scores for each orphan offer
  useEffect(() => {
    const calculateMatches = () => {
      const matches = orphanOffers.map(offer => {
        const prop = getOfferProperty(offer);
        const propertyAddress = prop.address;
        const propertyCity = prop.city;
        const propertyState = prop.state;
        const propertyZip = prop.zip;

        // Score each listing as a potential match
        const candidates: MatchCandidate[] = allListings
          .map(listing => {
            const reasons: string[] = [];
            let score = 0;

            // Exact address match (highest priority)
            if (
              propertyAddress &&
              listing.address &&
              propertyAddress.toLowerCase().includes(listing.address.toLowerCase())
            ) {
              score += 50;
              reasons.push('Address match');
            }

            // City + State match
            if (propertyCity && listing.city && propertyCity.toLowerCase() === listing.city.toLowerCase()) {
              score += 15;
              reasons.push('City match');
            }
            if (propertyState && listing.state && propertyState === listing.state) {
              score += 10;
              reasons.push('State match');
            }

            // ZIP code match
            if (propertyZip && listing.zipCode && propertyZip === listing.zipCode) {
              score += 20;
              reasons.push('ZIP match');
            }

            return { listing, matchScore: score, matchReasons: reasons };
          })
          .filter(c => c.matchScore > 0)
          .sort((a, b) => b.matchScore - a.matchScore);

        return {
          offer,
          candidates: candidates.slice(0, 5), // Top 5 matches
        };
      });

      setOrphansWithMatches(matches);
    };

    if (orphanOffers.length > 0) {
      calculateMatches();
    }
  }, [orphanOffers, allListings]);

  // Filter orphans based on search query
  const filteredOrphans = useMemo(() => {
    if (!searchQuery.trim()) return orphansWithMatches;

    const query = searchQuery.toLowerCase();
    return orphansWithMatches.filter(owm => {
      const prop = getOfferProperty(owm.offer);
      const address = prop.address;
      const city = prop.city;
      const lender =
        owm.offer.extractedData?.pre_approval?.lender_name ||
        owm.offer.combinedOfferData?.preApproval?.mortgageCompany?.name || '';

      return (
        address.toLowerCase().includes(query) ||
        city.toLowerCase().includes(query) ||
        lender.toLowerCase().includes(query)
      );
    });
  }, [orphansWithMatches, searchQuery]);

  const handleAutoMatchAndLink = async () => {
    setIsProcessing(true);
    let successCount = 0;

    try {
      // Find orphans with high-confidence matches (score > 30)
      const autoLinkCandidates = orphansWithMatches.filter(
        owm => owm.candidates.length > 0 && owm.candidates[0].matchScore > 30
      );

      for (const orphan of autoLinkCandidates) {
        const topCandidate = orphan.candidates[0];
        try {
          const offerRef = doc(db, 'offers', orphan.offer.id!);
          await updateDoc(offerRef, {
            listingId: topCandidate.listing.id,
            status: 'pending',
            orphanReason: null,
          });

          onOfferLinked(orphan.offer.id!, topCandidate.listing.id!);
          successCount++;
        } catch (err) {
          console.error(`Failed to link offer ${orphan.offer.id}:`, err);
        }
      }

      setLinkedCount(successCount);
      alert(`Successfully linked ${successCount} offers to listings`);

      // Remove linked offers from view
      setOrphansWithMatches(prev =>
        prev.filter(owm => !autoLinkCandidates.some(ac => ac.offer.id === owm.offer.id))
      );
    } catch (err) {
      console.error('Error in auto-matching:', err);
      alert('Error linking offers');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManualLinkSelected = async () => {
    setIsProcessing(true);
    let successCount = 0;

    try {
      const offerIds = Array.from(selectedOffers);
      
      for (const offerId of offerIds) {
        const orphan = orphansWithMatches.find(owm => owm.offer.id === offerId);
        if (!orphan || !orphan.selectedListingId) continue;

        try {
          const offerRef = doc(db, 'offers', offerId);
          await updateDoc(offerRef, {
            listingId: orphan.selectedListingId,
            status: 'pending',
            orphanReason: null,
          });

          onOfferLinked(offerId, orphan.selectedListingId);
          successCount++;
        } catch (err) {
          console.error(`Failed to link offer ${offerId}:`, err);
        }
      }

      setLinkedCount(successCount);
      alert(`Successfully linked ${successCount} offers to listings`);

      // Remove linked offers from view
      const linkedIds = new Set(selectedOffers);
      setOrphansWithMatches(prev => prev.filter(owm => !linkedIds.has(owm.offer.id!)));
      setSelectedOffers(new Set());
    } catch (err) {
      console.error('Error in manual linking:', err);
      alert('Error linking offers');
    } finally {
      setIsProcessing(false);
    }
  };;

  const handleSelectOffer = (offerId: string) => {
    const newSelected = new Set(selectedOffers);
    if (newSelected.has(offerId)) {
      newSelected.delete(offerId);
    } else {
      newSelected.add(offerId);
    }
    setSelectedOffers(newSelected);
  };

  const handleSelectListingForOffer = (offerId: string, listingId: string) => {
    setOrphansWithMatches(prev =>
      prev.map(owm =>
        owm.offer.id === offerId ? { ...owm, selectedListingId: listingId } : owm
      )
    );
  };

  const handleSelectAll = () => {
    if (selectedOffers.size === filteredOrphans.length) {
      setSelectedOffers(new Set());
    } else {
      setSelectedOffers(new Set(filteredOrphans.map(o => o.offer.id!)));
    }
  };

  const openCreateListingForm = (offerId: string, offer: Offer) => {
    const prop = getOfferProperty(offer);
    setCreateListingForms(prev => ({
      ...prev,
      [offerId]: {
        address: prop.address,
        city: prop.city,
        state: prop.state,
        zipCode: prop.zip,
      },
    }));
  };

  const closeCreateListingForm = (offerId: string) => {
    setCreateListingForms(prev => {
      const next = { ...prev };
      delete next[offerId];
      return next;
    });
  };

  const handleCreateAndLinkListing = async (offerId: string) => {
    if (!currentUser) return;
    const form = createListingForms[offerId];
    if (!form || !form.address.trim()) {
      alert('Please enter a property address');
      return;
    }

    setCreatingListingFor(offerId);
    try {
      // Create listing document
      const listingRef = await addDoc(collection(db, 'listings'), {
        userId: currentUser.uid,
        address: form.address.trim(),
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zipCode: form.zipCode.trim() || null,
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sourceType: 'created_from_offer',
      });

      // Link offer to new listing
      const offerRef = doc(db, 'offers', offerId);
      await updateDoc(offerRef, {
        listingId: listingRef.id,
        userId: currentUser.uid,
        status: 'pending',
        orphanReason: null,
      });

      onOfferLinked(offerId, listingRef.id);
      setLinkedCount(c => c + 1);
      closeCreateListingForm(offerId);
      setOrphansWithMatches(prev => prev.filter(owm => owm.offer.id !== offerId));
    } catch (err) {
      console.error('Error creating listing from offer:', err);
      alert('Error creating listing. Please try again.');
    } finally {
      setCreatingListingFor(null);
    }
  };

  if (orphanOffers.length === 0) {
    return (
      <div className="orphan-manager-modal-overlay" onClick={onClose}>
        <div className="orphan-manager-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Orphan Offer Manager</h2>
            <button className="close-button" onClick={onClose}>✕</button>
          </div>
          <div className="modal-content">
            <div className="empty-state">
              <div className="empty-icon">✨</div>
              <h3>All Offers Connected!</h3>
              <p>There are no orphan offers waiting to be connected to listings.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const selectedCount = selectedOffers.size;
  const unlinkedCount = orphansWithMatches.length;
  const autoMatchableCount = orphansWithMatches.filter(
    owm => owm.candidates.length > 0 && owm.candidates[0].matchScore > 30
  ).length;

  return (
    <div className="orphan-manager-modal-overlay" onClick={onClose}>
      <div className="orphan-manager-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔗 Orphan Offer Manager</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <div className="modal-toolbar">
          <div className="toolbar-section">
            <div className="stats">
              <div className="stat">
                <span className="stat-label">Unlinked:</span>
                <span className="stat-value">{unlinkedCount}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Auto-Matchable:</span>
                <span className="stat-value highlight">{autoMatchableCount}</span>
              </div>
              {selectedCount > 0 && (
                <div className="stat">
                  <span className="stat-label">Selected:</span>
                  <span className="stat-value">{selectedCount}</span>
                </div>
              )}
              {linkedCount > 0 && (
                <div className="stat">
                  <span className="stat-label">Linked:</span>
                  <span className="stat-value success">+{linkedCount}</span>
                </div>
              )}
            </div>
          </div>

          <div className="toolbar-section">
            <div className="view-toggle">
              <button
                className={`toggle-btn ${viewMode === 'auto-match' ? 'active' : ''}`}
                onClick={() => setViewMode('auto-match')}
              >
                🤖 Auto-Match
              </button>
              <button
                className={`toggle-btn ${viewMode === 'manual-link' ? 'active' : ''}`}
                onClick={() => setViewMode('manual-link')}
              >
                ✋ Manual Link
              </button>
            </div>
          </div>
        </div>

        <div className="modal-content">
          {viewMode === 'auto-match' && (
            <div className="auto-match-section">
              <div className="section-header">
                <h3>Automatic Matching</h3>
                <p>System will match offers to listings based on property address, city, and ZIP code</p>
              </div>

              {autoMatchableCount > 0 ? (
                <div className="auto-match-action">
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={handleAutoMatchAndLink}
                    disabled={isProcessing}
                  >
                    {isProcessing ? '⏳ Processing...' : `✓ Auto-Link ${autoMatchableCount} Offers`}
                  </button>
                  <p className="action-help">This will link {autoMatchableCount} offers with high-confidence matches</p>
                </div>
              ) : (
                <div className="empty-match-state">
                  <p>No high-confidence matches found. Try manual linking instead.</p>
                </div>
              )}

              <div className="match-preview">
                <h4>Preview of Auto-Matches</h4>
                <div className="preview-list">
                  {orphansWithMatches
                    .filter(owm => owm.candidates.length > 0 && owm.candidates[0].matchScore > 30)
                    .slice(0, 5)
                    .map(owm => (
                      <div key={owm.offer.id} className="preview-item">
                        <div className="preview-offer">
                          <div className="address-preview">
                            {getOfferProperty(owm.offer).address || 'Unknown Address'}
                          </div>
                          <div className="score-badge">
                            {owm.candidates[0].matchScore}% match
                          </div>
                        </div>
                        <div className="preview-arrow">→</div>
                        <div className="preview-listing">
                          <div className="listing-preview">
                            {owm.candidates[0].listing.address}
                          </div>
                          <div className="match-reasons">
                            {owm.candidates[0].matchReasons.join(', ')}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {viewMode === 'manual-link' && (
            <div className="manual-link-section">
              <div className="section-header">
                <h3>Manual Linking</h3>
                <p>Manually select and match offers to specific listings</p>
              </div>

              <div className="search-bar">
                <input
                  type="text"
                  placeholder="🔍 Search by address, city, or lender..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    className="clear-search"
                    onClick={() => setSearchQuery('')}
                  >
                    ✕
                  </button>
                )}
              </div>

              {filteredOrphans.length > 0 && (
                <div className="bulk-actions">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedOffers.size === filteredOrphans.length && filteredOrphans.length > 0}
                      onChange={handleSelectAll}
                    />
                    Select All ({filteredOrphans.length})
                  </label>
                  {selectedCount > 0 && (
                    <button
                      className="btn btn-primary"
                      onClick={handleManualLinkSelected}
                      disabled={isProcessing || selectedCount === 0}
                    >
                      {isProcessing ? '⏳ Linking...' : `✓ Link ${selectedCount} Selected`}
                    </button>
                  )}
                </div>
              )}

              <div className="orphan-list">
                {filteredOrphans.length > 0 ? (
                  filteredOrphans.map(owm => (
                    <div key={owm.offer.id} className="orphan-card">
                      <div className="card-header">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedOffers.has(owm.offer.id!)}
                            onChange={() => handleSelectOffer(owm.offer.id!)}
                          />
                          <span className="offer-address">
                            {getOfferProperty(owm.offer).address || 'Unknown Address'}
                          </span>
                        </label>
                        <div className="offer-meta">
                          <span className="offer-date">
                            {owm.offer.submissionDate?.toLocaleDateString()}
                          </span>
                          <span className="offer-lender">
                            {owm.offer.extractedData?.pre_approval?.lender_name ||
                              owm.offer.combinedOfferData?.preApproval?.mortgageCompany?.name ||
                              'Unknown Lender'}
                          </span>
                        </div>
                      </div>

                      {selectedOffers.has(owm.offer.id!) && (
                        <div className="card-content">
                          <div className="matches-section">
                            <label className="section-label">Select a listing to link:</label>
                            {owm.candidates.length > 0 ? (
                              <div className="candidates-list">
                                {owm.candidates.map(candidate => (
                                  <label
                                    key={candidate.listing.id}
                                    className={`candidate-option ${
                                      owm.selectedListingId === candidate.listing.id ? 'selected' : ''
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name={`listing-${owm.offer.id}`}
                                      value={candidate.listing.id}
                                      checked={owm.selectedListingId === candidate.listing.id}
                                      onChange={() =>
                                        handleSelectListingForOffer(owm.offer.id!, candidate.listing.id!)
                                      }
                                    />
                                    <div className="candidate-info">
                                      <div className="candidate-address">{candidate.listing.address}</div>
                                      <div className="candidate-meta">
                                        {candidate.listing.city}, {candidate.listing.state}{' '}
                                        {candidate.listing.zipCode}
                                      </div>
                                      <div className="match-score">
                                        {candidate.matchScore}% match • {candidate.matchReasons.join(', ')}
                                      </div>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <div className="no-candidates">
                                <p>No matching listings found</p>
                              </div>
                            )}
                          </div>

                          {/* Link to existing listing */}
                          <div className="all-listings-section">
                            <label className="section-label">
                              {owm.candidates.length === 0 ? 'Select from all listings:' : 'Or choose another listing:'}
                            </label>
                            <div className="all-listings-dropdown">
                              <select
                                value={owm.selectedListingId || ''}
                                onChange={e =>
                                  handleSelectListingForOffer(owm.offer.id!, e.target.value)
                                }
                                className="listing-select"
                              >
                                <option value="">-- Choose a listing --</option>
                                {allListings.map(listing => (
                                  <option key={listing.id} value={listing.id}>
                                    {listing.address}, {listing.city}, {listing.state}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Create new listing from offer data */}
                          <div className="create-listing-section">
                            {!createListingForms[owm.offer.id!] ? (
                              <button
                                className="btn btn-outline-create"
                                onClick={() => openCreateListingForm(owm.offer.id!, owm.offer)}
                              >
                                + Create New Listing from This Offer
                              </button>
                            ) : (
                              <div className="create-listing-form">
                                <h5 className="create-listing-title">New Listing Details</h5>
                                <div className="form-row">
                                  <label>Address *</label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={createListingForms[owm.offer.id!].address}
                                    onChange={e =>
                                      setCreateListingForms(prev => ({
                                        ...prev,
                                        [owm.offer.id!]: { ...prev[owm.offer.id!], address: e.target.value },
                                      }))
                                    }
                                    placeholder="Street address"
                                  />
                                </div>
                                <div className="form-row-inline">
                                  <div className="form-field">
                                    <label>City</label>
                                    <input
                                      type="text"
                                      className="form-input"
                                      value={createListingForms[owm.offer.id!].city}
                                      onChange={e =>
                                        setCreateListingForms(prev => ({
                                          ...prev,
                                          [owm.offer.id!]: { ...prev[owm.offer.id!], city: e.target.value },
                                        }))
                                      }
                                    />
                                  </div>
                                  <div className="form-field form-field-sm">
                                    <label>State</label>
                                    <input
                                      type="text"
                                      className="form-input"
                                      value={createListingForms[owm.offer.id!].state}
                                      onChange={e =>
                                        setCreateListingForms(prev => ({
                                          ...prev,
                                          [owm.offer.id!]: { ...prev[owm.offer.id!], state: e.target.value },
                                        }))
                                      }
                                      maxLength={2}
                                    />
                                  </div>
                                  <div className="form-field form-field-sm">
                                    <label>ZIP</label>
                                    <input
                                      type="text"
                                      className="form-input"
                                      value={createListingForms[owm.offer.id!].zipCode}
                                      onChange={e =>
                                        setCreateListingForms(prev => ({
                                          ...prev,
                                          [owm.offer.id!]: { ...prev[owm.offer.id!], zipCode: e.target.value },
                                        }))
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="form-actions">
                                  <button
                                    className="btn btn-primary"
                                    onClick={() => handleCreateAndLinkListing(owm.offer.id!)}
                                    disabled={creatingListingFor === owm.offer.id!}
                                  >
                                    {creatingListingFor === owm.offer.id! ? 'Creating...' : 'Create & Link Listing'}
                                  </button>
                                  <button
                                    className="btn btn-secondary"
                                    onClick={() => closeCreateListingForm(owm.offer.id!)}
                                    disabled={creatingListingFor === owm.offer.id!}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    <p>No offers match your search</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrphanOfferManager;
